import React, { useState, useEffect, useRef } from 'react';
import { CardData } from '../types';
import { getLayoutTheme } from '../utils/layoutColors';
import { getReadableTextColor } from './ActivityNode/colorUtils';
import { ClassicDecisionShape } from './ClassicShapes';
import { Square, Diamond, Link as LinkIcon, Check } from 'lucide-react';
import { CARD_THEMES } from './ActivityNode/cardThemes';
import { useProjectStore } from '../store/useProjectStore';
import { useTheme } from '../context/ThemeContext';
import { getHeatmapSoftColor, getHeatmapColor } from '../utils/heatmap';

// Constante para evitar nova referência de array vazio a cada render
const EMPTY_CARDS_ARRAY: CardData[] = [];

// Funções auxiliares para manipulação de cores
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

// Função para aumentar contraste de uma cor
const increaseContrast = (hex: string, contrastPercent: number = 20): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const factor = 1 - (contrastPercent / 100);
  const newR = Math.max(0, Math.min(255, Math.round(rgb.r * factor)));
  const newG = Math.max(0, Math.min(255, Math.round(rgb.g * factor)));
  const newB = Math.max(0, Math.min(255, Math.round(rgb.b * factor)));
  return rgbToHex(newR, newG, newB);
};

// Função para converter qualquer cor para o mesmo tom do amarelo padrão do post-it
const convertToPostItTone = (hex: string): { bg: string; border: string; footer: string } => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return {
      bg: '#fff9c4',
      border: '#e3d081',
      footer: '#fff59d'
    };
  }
  
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const bgRgb = hslToRgb(hsl.h, 100, 88);
  const borderRgb = hslToRgb(hsl.h, 64, 70);
  const footerRgb = hslToRgb(hsl.h, 100, 81);
  
  return {
    bg: rgbToHex(bgRgb.r, bgRgb.g, bgRgb.b),
    border: rgbToHex(borderRgb.r, borderRgb.g, borderRgb.b),
    footer: rgbToHex(footerRgb.r, footerRgb.g, footerRgb.b)
  };
};

interface DecisionNodeProps {
  data: CardData;
  selected: boolean;
  isDragging: boolean;
  isConnecting: boolean;
  cardWidth: number;
  cardHeight: number;
  decisionSize: number;
  handlePointerDown: (e: React.PointerEvent) => void;
  handlePointerMove: (e: React.PointerEvent) => void;
  handlePointerUp: (e: React.PointerEvent) => void;
  onFinishConnection: (targetId: string) => void;
  onAddNext?: (sourceId: string, type: 'atividade' | 'decisao', isYesPath?: boolean) => void;
  onStartConnection?: (id: string, handle: 'right' | 'bottom') => void;
  onSelect?: (id: string, e?: React.MouseEvent) => void; // Handler para selecionar o card
}

export const DecisionNode: React.FC<DecisionNodeProps> = ({
  data,
  selected,
  isDragging,
  isConnecting,
  cardWidth,
  cardHeight,
  decisionSize,
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
  onFinishConnection,
  onAddNext,
  onStartConnection,
  onSelect,
}) => {
  const [isHovered, setIsHovered] = useState(false); // Estado para hover do card
  const [showAddMenuYes, setShowAddMenuYes] = useState(false); // Menu do botão SIM
  const [showAddMenuNo, setShowAddMenuNo] = useState(false); // Menu do botão NÃO
  const [isMenuYesAnimating, setIsMenuYesAnimating] = useState(false);
  const [isMenuNoAnimating, setIsMenuNoAnimating] = useState(false);

  const heatmapMode = useProjectStore(state => state.heatmapMode);
  const mapType = useProjectStore(state => state.mapType);
  const cards = useProjectStore(state => {
    const activeSheet = state.sheets.find(s => s.id === state.activeSheetId);
    return activeSheet?.cards ?? EMPTY_CARDS_ARRAY;
  });

  // Subscricao direta ao card na store â€” re-render imediato sem esperar prop cascade
  const liveCard = useProjectStore(state => {
    const sheet = state.sheets.find(s => s.id === state.activeSheetId);
    return sheet?.cards.find(c => c.id === data.id) ?? null;
  });
  const effectiveAtividade = liveCard?.atividade ?? data.atividade ?? '';
  const effectiveNomeFluxo = liveCard?.nome_fluxo ?? data.nome_fluxo ?? '';
  const effectiveDecisaoSimLabel = (liveCard as any)?.decisao_sim_label ?? (data as any).decisao_sim_label ?? 'Sim';
  const effectiveDecisaoNaoLabel = (liveCard as any)?.decisao_nao_label ?? (data as any).decisao_nao_label ?? 'Não';
  const effectiveDecisaoSimPct = (liveCard as any)?.decisao_sim_pct ?? (data as any).decisao_sim_pct ?? 0;
  const effectiveDecisaoNaoPct = (liveCard as any)?.decisao_nao_pct ?? (data as any).decisao_nao_pct ?? 0;

  // Cálculo do Heatmap
  const traInMinutes = Number(data.tt) || 0;
  
  const maxLoad = React.useMemo(() => {
    if (!heatmapMode) return 0;
    return Math.max(...cards.map(c => Number(c.tt) || 0), 1);
  }, [cards, heatmapMode]);

  const heatmapBg = heatmapMode && mapType !== 'FACTORY' ? getHeatmapSoftColor(traInMinutes, maxLoad) : undefined;
  const heatmapBorder = heatmapMode && mapType !== 'FACTORY' ? getHeatmapColor(traInMinutes, maxLoad) : undefined;

  const menuYesRef = useRef<HTMLDivElement>(null);
  const menuNoRef = useRef<HTMLDivElement>(null);

  // Fechar hover quando seleciona outro card ou quando selected muda para false
  useEffect(() => {
    if (!selected) {
      setIsHovered(false);
      setShowAddMenuYes(false);
      setShowAddMenuNo(false);
      setIsMenuYesAnimating(false);
      setIsMenuNoAnimating(false);
    }
  }, [selected]);

  // Controlar animação de entrada dos menus
  useEffect(() => {
    if (showAddMenuYes) {
      setIsMenuYesAnimating(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsMenuYesAnimating(false);
        });
      });
    } else {
      setIsMenuYesAnimating(false);
    }
  }, [showAddMenuYes]);

  useEffect(() => {
    if (showAddMenuNo) {
      setIsMenuNoAnimating(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsMenuNoAnimating(false);
        });
      });
    } else {
      setIsMenuNoAnimating(false);
    }
  }, [showAddMenuNo]);

  // Fechar menus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        showAddMenuYes &&
        menuYesRef.current &&
        !menuYesRef.current.contains(target) &&
        !target.closest(`[data-card-id="${data.id}"]`)
      ) {
        setShowAddMenuYes(false);
      }
      if (
        showAddMenuNo &&
        menuNoRef.current &&
        !menuNoRef.current.contains(target) &&
        !target.closest(`[data-card-id="${data.id}"]`)
      ) {
        setShowAddMenuNo(false);
      }
    };

    if (showAddMenuYes || showAddMenuNo) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAddMenuYes, showAddMenuNo, data.id]);
  
  // Estilos do Usuário (Office Style)
  const userBg = data.style?.backgroundColor;
  const userTextColor = data.style?.textColor;
  const userBold = data.style?.isBold ? 'font-bold' : 'font-medium';
  const userItalic = data.style?.isItalic ? 'italic' : '';
  const userFontSize = data.style?.fontSize || 13;

  // Obter layoutType (com fallback para 'default')
  const layoutType = data.layoutType || 'default';

  // Mapa de cores do Ribbon → hex (espelho do ActivityNode)
  const ribbonColorBgMap: Record<string, string> = {
    'white': '#ffffff',
    'gray-10': '#f3f4f6', 'gray-20': '#e5e7eb', 'gray': '#e5e7eb',
    'yellow-10': '#fefce8', 'yellow-20': '#fef9c3', 'yellow': '#fef08a', 'yellow-dark': '#fde047',
    'red-10': '#fef2f2', 'red-20': '#fee2e2', 'red': '#fecaca', 'red-dark': '#fca5a5',
    'green-10': '#ecfdf5', 'green-20': '#d1fae5', 'green': '#bbf7d0', 'green-dark': '#86efac',
    'blue-10': '#eff6ff', 'blue-20': '#dbeafe', 'blue': '#bfdbfe', 'blue-dark': '#93c5fd',
    'emerald': '#ecfdf5', 'ocean': '#eff6ff', 'orange': '#fff7ed', 'amber': '#fefce8',
    'coral': '#fef2f2', 'purple': '#faf5ff', 'lavender': '#faf5ff', 'forest': '#f0fdf4',
    'sage': '#f0fdf4', 'sunset': '#fff7ed', 'midnight': '#f1f5f9',
  };
  const ribbonColorBg = data.layoutColor
    ? (data.layoutColor.startsWith('#') ? data.layoutColor : ribbonColorBgMap[data.layoutColor])
    : undefined;

  // Helper: fundo postit escuro? (mesma regra luminância do Início/label principal)
  const postItDarkBg = layoutType === 'postit' && ribbonColorBg && ribbonColorBg !== '#ffffff'
    ? (() => {
        const h = ribbonColorBg.replace('#', '');
        if (h.length === 6) {
          const r = parseInt(h.substring(0,2),16), g = parseInt(h.substring(2,4),16), b = parseInt(h.substring(4,6),16);
          return (r*299+g*587+b*114)/1000 < 140;
        }
        return false;
      })()
    : false;

  // Helper: cor muito clara → borda preta
  const isVeryLight = (hex: string): boolean => {
    const h = hex.replace('#', '');
    if (h.length !== 6) return false;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 230;
  };

  // ETAPA 1: Converter formato do Ribbon para formato do layoutColors
  // (Ribbon usa 'color-dark', layoutColors usa 'darkcolor')
  const normalizeColorName = (color: string | undefined): string | undefined => {
    if (!color) return undefined;
    
    const colorMap: Record<string, string> = {
      'grey': 'gray',              // Sinônimo
      'red-dark': 'darkred',       // Converter formato
      'blue-dark': 'darkblue',
      'green-dark': 'darkgreen',
      'yellow-dark': 'darkyellow',
      'blue-light': 'lightblue',
      'green-light': 'lightgreen',
    };
    
    return colorMap[color] || color;
  };
  
  // ETAPA 2: Remover sufixo numérico (ex: "yellow-10" -> "yellow", "gray-20" -> "gray")
  const cleanLayoutColor = data.layoutColor 
    ? normalizeColorName(data.layoutColor.replace(/-\d+$/, ''))
    : undefined;

  // Obter tema de cores do layout (se houver)
  // Para modo clássico, buscar tema do modo 'default' para obter as cores
  const layoutTheme = layoutType && cleanLayoutColor
    ? (layoutType === 'classic' 
        ? getLayoutTheme('default', cleanLayoutColor)  // Buscar tema default para cores
        : getLayoutTheme(layoutType, cleanLayoutColor))
    : null;
  
  if (data.layoutColor && !layoutTheme) {
  } else if (data.layoutColor && layoutTheme) {
  }
  
  // Função para clarear cores no modo clássico (melhor legibilidade)
  const lightenColorForClassic = (color: string): string => {
    const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(color);
    if (!rgb) return color;
    
    // Aumentar cada canal em 60% em direção ao branco (255)
    const lighten = (val: number) => Math.min(255, Math.round(val + (255 - val) * 0.6));
    const r = lighten(rgb.r);
    const g = lighten(rgb.g);
    const b = lighten(rgb.b);
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // No modo clássico, usar cores do CARD_THEMES se layoutColor estiver definido, seNão usar ribbonColorBg ou userBg
  const classicBg = layoutType === 'classic' 
    ? (data.layoutColor && CARD_THEMES[data.layoutColor] ? CARD_THEMES[data.layoutColor].headerBg : (ribbonColorBg || userBg))
    : userBg;

  // Smart text: auto branco/preto conforme luminância do fundo (modo clássico)
  const classicEffectiveBg = classicBg || '#ffffff';
  const classicSmartText = getReadableTextColor(classicEffectiveBg);
  const isDarkBg = classicSmartText === '#ffffff';
  const classicSmartTextDim = isDarkBg ? 'rgba(255,255,255,0.60)' : 'rgba(0,0,0,0.45)';
  const classicSimColor = isDarkBg ? '#93c5fd' : '#2563eb';
  const { isDark, theme: appTheme } = useTheme();
  const themeStrokeColor = isDark ? '#ffffff' : '#000000';
  const themeCanvasBg = appTheme === 'black' ? '#131314' : isDark ? '#223244' : '#ffffff';
  const classicNaoColor = isDarkBg ? '#fca5a5' : '#dc2626';
  
  // Cores padrão para fallback
  const defaultPostItTheme = {
    bg: '#fff9c4',
    border: '#e3d081',
    footerBg: '#fff59d',
    text: '#1f2937',
    textSecondary: '#64748b',
  };
  
  const defaultDefaultTheme = {
    bg: '#ffffff',
    border: '#10b981',
    headerBg: '#10b981',
    text: '#1f2937',
    textSecondary: '#64748b',
  };
  
  const defaultMinimalTheme = {
    bg: '#f0f9ff',
    border: '#0ea5e9',
    text: '#0c4a6e',
    textSecondary: '#0284c7',
    accent: '#38bdf8',
  };
  
  // Tema atual baseado no layout (com fallback)
  const postItTheme = layoutType === 'postit' 
    ? (layoutTheme || defaultPostItTheme)
    : defaultPostItTheme;
  
  const defaultTheme = layoutType === 'default'
    ? (layoutTheme || defaultDefaultTheme)
    : defaultDefaultTheme;
  
  const minimalTheme = layoutType === 'minimal'
    ? (layoutTheme || defaultMinimalTheme)
    : defaultMinimalTheme;

  // Cores para o modo Post-it (com userBg ou layoutColor)
  let postItBgColor: string | undefined;
  let postItBorderColor: string | undefined;
  
  // Cor efetiva: ribbonColorBg tem prioridade sobre userBg
  const effectivePostItColor = ribbonColorBg || userBg;

  if (layoutType === 'postit') {
    const isWhiteColor = cleanLayoutColor === 'white'
      || effectivePostItColor === '#ffffff'
      || effectivePostItColor === '#fff'
      || (effectivePostItColor ? isVeryLight(effectivePostItColor) : false);

    if (isWhiteColor) {
      postItBgColor = '#ffffff';
      postItBorderColor = '#000000';
    } else if (effectivePostItColor) {
      const postItTone = convertToPostItTone(effectivePostItColor);
      postItBgColor = postItTone.bg;
      postItBorderColor = postItTone.border;
    } else if (layoutTheme) {
      postItBgColor = layoutTheme.bg;
      postItBorderColor = layoutTheme.border;
    } else {
      postItBgColor = postItTheme.bg;
      postItBorderColor = postItTheme.border;
    }
  }

  return (
    <div
      className="w-full h-full flex items-center justify-center relative bg-transparent group hover:-translate-y-1 transition-all duration-300 overflow-visible"
      data-card-id={data.id}
      data-card-type="decisao"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onMouseEnter={() => {
        // Abrir menus automaticamente no hover
        setIsHovered(true);
        if (!isDragging && !isConnecting && onAddNext) {
          setShowAddMenuYes(true);
          setShowAddMenuNo(true);
        }
      }}
      onMouseLeave={(e) => {
        // Verificar se o mouse está saindo para os menus ou outro elemento do mesmo card
        const relatedTarget = e.relatedTarget;
        const el = relatedTarget instanceof Element ? relatedTarget : null;
        const stayingInCardOrMenu = 
          el?.closest('.nodrag') || 
          el?.closest(`[data-card-id="${data.id}"]`) ||
          el?.closest(`[data-menu-yes="${data.id}"]`) ||
          el?.closest(`[data-menu-no="${data.id}"]`);
        if (!stayingInCardOrMenu) {
          setIsHovered(false);
          setShowAddMenuYes(false);
          setShowAddMenuNo(false);
        }
      }}
      onClick={(e) => { 
        if(isConnecting) { 
          e.stopPropagation(); 
          e.preventDefault();
          onFinishConnection(data.id); 
        }
      }}
    >
      {/* Modo Clássico (VSM) */}
      {layoutType === 'classic' ? (
        <>
          {/* Losango no modo clássico: usar SVG fornecido - centralizado */}
          {/* Usar 160px para manter mesmo tamanho visual que o modo padrão */}
          <div 
            className="absolute" 
            data-decision-content="true"
            style={{ 
              width: 160, 
              height: 160, 
              pointerEvents: 'none',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            <ClassicDecisionShape width={160} height={160} userBg={heatmapBg || classicBg || (isDark ? themeCanvasBg : undefined)} selected={selected} themeStrokeColor={themeStrokeColor} />
          </div>
          {/* Textos dentro do losango no modo clássico - similar ao modo padrão */}
          <>
            {/* Card ID no topo do losango */}
            {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
              <div
                className="absolute flex items-center justify-center z-10 pointer-events-none"
                data-decision-content="true"
                style={{ top: '28px', left: 0, right: 0 }}
              >
                <span style={{
                  fontSize: '9px',
                  fontWeight: 400,
                  fontFamily: "'Roboto Mono', monospace",
                  letterSpacing: '0.05em',
                  opacity: 0.9,
                  color: userTextColor || classicSmartText,
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}>
                  #{String(data.numeroSequencial).padStart(3, '0')}
                </span>
              </div>
            )}
            {/* Conteúdo central: apenas o nome da atividade, centralizado dentro do losango */}
            <div 
              className="absolute flex items-center justify-center text-center px-4 z-10 pointer-events-none"
              data-decision-content="true"
              style={{
                inset: 0
              }}
            >
              <div
                className="leading-tight line-clamp-3"
                style={{ 
                  fontSize: `${Math.max(9, (userFontSize || 13) * 0.75)}px`, // Reduzir 25% do tamanho original
                  color: userTextColor || classicSmartText,
                  fontWeight: userBold === 'font-bold' ? 'bold' : 500,
                  fontStyle: userItalic === 'italic' ? 'italic' : 'normal'
                }}
              >
                {effectiveAtividade || effectiveNomeFluxo || 'Nova Decisão'}
              </div>
            </div>
            {/* Rótulos de SIM / NÃO - posicionados dentro do losango, similar aos outros modos */}
            <div 
              className="absolute flex flex-col items-end z-10 pointer-events-none"
              data-decision-content="true"
              style={{
                right: '1rem',
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            >
              <span style={{ fontSize: '10px', fontWeight: 'bold', color: classicSimColor }}>
                {effectiveDecisaoSimLabel}
              </span>
              <span style={{ fontSize: '8px', color: classicSmartTextDim }}>
                {effectiveDecisaoSimPct}%
              </span>
            </div>
            <div 
              className="absolute flex flex-col items-center z-10 pointer-events-none"
              data-decision-content="true"
              style={{
                bottom: '1rem',
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            >
              <span style={{ fontSize: '10px', fontWeight: 'bold', color: classicNaoColor }}>
                {effectiveDecisaoNaoLabel}
              </span>
              <span style={{ fontSize: '8px', color: classicSmartTextDim }}>
                {effectiveDecisaoNaoPct}%
              </span>
            </div>
          </>
        </>
      ) : (
        <>
          {/* Losango - usa dimensões: 200px no modo clássico, 160px nos outros modos */}
          {/* Container para o losango com contorno SVG que segue exatamente a forma */}
          <div
            className="absolute transition-all duration-200 ease-in-out"
            data-decision-content="true"
            style={{
              width: decisionSize,
              height: decisionSize,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            {/* Contorno SVG que segue exatamente a forma do losango */}
            <svg
              width={decisionSize}
              height={decisionSize}
              style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}
              data-decision-content="true"
            >
              {/* Padrão diagonal para o modo minimal â€” mesmo efeito do card Início */}
              {layoutType === 'minimal' && (
                <defs>
                  <pattern id={`diagKn-${data.id}`} width="8" height="8" patternUnits="userSpaceOnUse">
                    <line x1="0" y1="8" x2="8" y2="0" stroke="rgba(0,0,0,0.07)" strokeWidth="1"/>
                  </pattern>
                </defs>
              )}
              {/* Fundo do losango - polígono preenchido */}
              <polygon
                points={`${decisionSize/2},0 ${decisionSize},${decisionSize/2} ${decisionSize/2},${decisionSize} 0,${decisionSize/2}`}
                fill={(() => {
                  if (heatmapBg) {
                    return heatmapBg;
                  } else if (layoutType === 'postit') {
                    // Mesmo que o card Início postit: ribbonColorBg direto, depois postItBgColor (tint)
                    return ribbonColorBg || postItBgColor || postItTheme.bg;
                  } else if (layoutType === 'default') {
                    // Ficha: fundo branco â€” cor aparece apenas na borda (igual ao body do card atividade)
                    return '#ffffff';
                  } else if (layoutType === 'minimal') {
                    if (cleanLayoutColor === 'white' || ribbonColorBg === '#ffffff') return '#ffffff';
                    // Mesma lógica toLight() usada no card Início do compacto (88% blend para branco)
                    const toLight = (hex: string): string => {
                      const h = hex.replace('#', '');
                      if (h.length !== 6) return hex;
                      const r = parseInt(h.substring(0, 2), 16);
                      const g = parseInt(h.substring(2, 4), 16);
                      const b = parseInt(h.substring(4, 6), 16);
                      const blend = (c: number) => Math.round(c + (255 - c) * 0.88);
                      return `rgb(${blend(r)}, ${blend(g)}, ${blend(b)})`;
                    };
                    const accent = ribbonColorBg || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border;
                    return accent ? toLight(accent) : minimalTheme.bg;
                  } else if (layoutType === 'neon') {
                    return '#0d1117';
                  } else if (layoutType === 'blueprint') {
                    return '#0b1929';
                  } else if (layoutType === 'origami') {
                    return '#fffdf7';
                  } else if (layoutType === 'kanban') {
                    return '#ffffff';
                  } else if (layoutType === 'ticket') {
                    return '#fffef5'; // creme, igual ao body do card Ticket
                  } else {
                    return '#ffffff';
                  }
                })()}
                stroke="none"
              />
              {/* Padrão diagonal overlay â€” apenas no modo minimal */}
              {layoutType === 'minimal' && !heatmapBg && (
                <polygon
                  points={`${decisionSize/2},0 ${decisionSize},${decisionSize/2} ${decisionSize/2},${decisionSize} 0,${decisionSize/2}`}
                  fill={`url(#diagKn-${data.id})`}
                  stroke="none"
                />
              )}
              {/* Losango: contorno */}
              <polygon
                points={`${decisionSize/2},0 ${decisionSize},${decisionSize/2} ${decisionSize/2},${decisionSize} 0,${decisionSize/2}`}
                fill="none"
                stroke={(() => {
                  // Determinar cor da borda baseada no estado e tema
                  if (selected) {
                    return '#3b82f6'; // Azul quando selecionado
                  }

                  // DECISÕES NÃO DEVEM SER AFETADAS PELO HEATMAP
                  // Sempre usar cor do tema/layout do usuário
                  
                  if (layoutType === 'postit') {
                    // Borda consistente com activity card postit
                    if (ribbonColorBg) {
                      if (ribbonColorBg === '#ffffff' || isVeryLight(ribbonColorBg)) return '#000000';
                      return increaseContrast(ribbonColorBg, 30);
                    }
                    return postItBorderColor || postItTheme.border;
                  } else if (layoutType === 'default') {
                    const c = ribbonColorBg || userBg;
                    if (!c || c === '#ffffff' || c === '#fff' || isVeryLight(c)) return '#000000';
                    if (ribbonColorBg) return increaseContrast(ribbonColorBg, 25);
                    if (layoutTheme) return layoutTheme.border;
                    if (userBg) return increaseContrast(userBg, 35);
                    return defaultTheme.border;
                  } else if (layoutType === 'minimal') {
                    if (!ribbonColorBg && cleanLayoutColor === 'white') return '#000000';
                    // Mesma lógica do ActivityNode minimal: usar accent direto sem escurecer
                    const accent = ribbonColorBg || (layoutTheme?.accent) || minimalTheme.accent || minimalTheme.border;
                    if (!accent || accent === '#ffffff' || isVeryLight(accent)) return '#000000';
                    return accent;
                  } else if (layoutType === 'neon') {
                    const neonAcc = ribbonColorBg || '#00e5ff';
                    return neonAcc;
                  } else if (layoutType === 'blueprint') {
                    return ribbonColorBg || '#3a7bd5';
                  } else if (layoutType === 'origami') {
                    const ogAcc = ribbonColorBg || '#e85d3a';
                    return ogAcc;
                  } else if (layoutType === 'kanban') {
                    const raw = ribbonColorBg || '#7c3aed';
                    // Escurecer se for claro
                    const h = raw.replace('#', '');
                    if (h.length === 6) {
                      const r = parseInt(h.substring(0,2),16), g = parseInt(h.substring(2,4),16), b = parseInt(h.substring(4,6),16);
                      const lum = (r*299+g*587+b*114)/1000;
                      if (lum > 140) {
                        const f = lum > 220 ? 0.25 : lum > 180 ? 0.40 : 0.55;
                        const nr = Math.round(r*f), ng = Math.round(g*f), nb = Math.round(b*f);
                        return '#'+[nr,ng,nb].map(x=>x.toString(16).padStart(2,'0')).join('');
                      }
                    }
                    return raw;
                  } else if (layoutType === 'ticket') {
                    // Mesma lógica darkenIfLight do card Ticket
                    const raw = ribbonColorBg || '#d97706';
                    const h = raw.replace('#', '');
                    if (h.length === 6) {
                      const r = parseInt(h.substring(0,2),16), g = parseInt(h.substring(2,4),16), b = parseInt(h.substring(4,6),16);
                      const lum = (r*299+g*587+b*114)/1000;
                      if (lum > 140) {
                        const f = lum > 220 ? 0.25 : lum > 180 ? 0.40 : 0.55;
                        const nr = Math.round(r*f), ng = Math.round(g*f), nb = Math.round(b*f);
                        return '#'+[nr,ng,nb].map(x=>x.toString(16).padStart(2,'0')).join('');
                      }
                    }
                    return raw;
                  } else {
                    return '#10b981';
                  }
                })()}
                strokeWidth={layoutType === 'blueprint' ? '2' : layoutType === 'origami' ? '2' : layoutType === 'minimal' ? '2' : layoutType === 'neon' ? '2.5' : layoutType === 'postit' ? '2' : layoutType === 'default' ? '2' : layoutType === 'ticket' ? '2' : '3'}
                strokeDasharray={layoutType === 'blueprint' ? '6 3' : undefined}
                filter={layoutType === 'neon' ? `drop-shadow(0 0 4px ${ribbonColorBg || '#00e5ff'}) drop-shadow(0 0 8px ${ribbonColorBg || '#00e5ff'}44)` : undefined}
                strokeLinejoin="miter"
                strokeLinecap="square"
              />
            </svg>
            
            {/* Número do card â€” topo do losango */}
            {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
              <div
                className="absolute z-30 pointer-events-none"
                data-decision-content="true"
                style={{ top: '16px', left: '50%', transform: 'translateX(-50%)' }}
              >
                <span style={{ fontSize: '8px', fontWeight: 700, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.05em', lineHeight: 1, opacity: 0.85, userSelect: 'none', whiteSpace: 'nowrap',
                  color: layoutType === 'blueprint' ? '#a8d4f0' : layoutType === 'neon' ? '#f0f8ff' : layoutType === 'postit' && postItDarkBg ? 'rgba(255,255,255,0.75)' : undefined }}>
                  #{String(data.numeroSequencial).padStart(3, '0')}
                </span>
              </div>
            )}

            {/* Conteúdo central DENTRO do container do losango */}
            <div 
              className="absolute inset-0 flex items-center justify-center text-center pointer-events-none px-4 z-20"
              data-decision-content="true"
            >
              <div
                className={`leading-tight line-clamp-3 ${userBold} ${userItalic}`}
                style={{ 
                  fontSize: `${Math.max(10, userFontSize - 2)}px`,
                  color: userTextColor || (() => {
                    if (layoutType === 'postit') {
                      if (cleanLayoutColor === 'white') return '#000000';
                      // Se ribbonColorBg sólido, usar contraste adequado (igual ao Início postit)
                      if (ribbonColorBg && ribbonColorBg !== '#ffffff') {
                        const h = ribbonColorBg.replace('#', '');
                        if (h.length === 6) {
                          const r = parseInt(h.substring(0,2),16), g = parseInt(h.substring(2,4),16), b = parseInt(h.substring(4,6),16);
                          return (r*299+g*587+b*114)/1000 < 140 ? '#ffffff' : '#000000';
                        }
                      }
                      if (layoutTheme) return layoutTheme.text;
                      if (userBg) {
                        const bgColor = userBg.toLowerCase().trim();
                        const isWhite = bgColor === '#ffffff' || bgColor === '#fff' || bgColor === 'white';
                        return isWhite ? '#000000' : postItTheme.text;
                      }
                      return postItTheme.text;
                    }
                    if (layoutType === 'default') {
                      if (cleanLayoutColor === 'white') return '#000000';
                      if (layoutTheme) return layoutTheme.text;
                      if (userBg) {
                        const bgColor = userBg.toLowerCase().trim();
                        const isWhite = bgColor === '#ffffff' || bgColor === '#fff' || bgColor === 'white';
                        return isWhite ? '#000000' : defaultTheme.text;
                      }
                      return defaultTheme.text;
                    }
                    if (layoutType === 'minimal') {
                      if (cleanLayoutColor === 'white') return '#000000';
                      return layoutTheme?.text || minimalTheme.text;
                    }
                    if (layoutType === 'neon') return '#f0f8ff';
                    if (layoutType === 'blueprint') return '#f0f8ff';
                    if (layoutType === 'origami') return '#1c1917';
                    if (layoutType === 'kanban') return '#18181b';
                    if (layoutType === 'ticket') return '#1a1a1a';
                    return '#000000';
                  })()
                }}
              >
                {effectiveAtividade || effectiveNomeFluxo || 'Nova Decisão'}
              </div>
            </div>
            
            {/* Rótulo SIM - posicionado à direita dentro do losango */}
            <div 
              className="absolute flex flex-col items-end z-30 pointer-events-none"
              data-decision-content="true"
              style={{ right: '8px', top: '50%', transform: 'translateY(-50%)' }}
            >
              <span style={{ fontSize: '10px', fontWeight: 'bold', color: layoutType === 'postit' && postItDarkBg ? 'rgba(255,255,255,0.92)' : '#2563eb' }}>
                {effectiveDecisaoSimLabel}
              </span>
              <span style={{ fontSize: '8px', color: layoutType === 'neon' || layoutType === 'blueprint' ? 'rgba(255,255,255,0.55)' : layoutType === 'kanban' ? '#71717a' : layoutType === 'postit' && postItDarkBg ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
                {effectiveDecisaoSimPct}%
              </span>
            </div>
            
            {/* Rótulo NÃO - posicionado embaixo dentro do losango */}
            <div 
              className="absolute flex flex-col items-center z-30 pointer-events-none"
              data-decision-content="true"
              style={{ bottom: '8px', left: '50%', transform: 'translateX(-50%)' }}
            >
              <span style={{ fontSize: '10px', fontWeight: 'bold', color: layoutType === 'postit' && postItDarkBg ? 'rgba(255,255,255,0.92)' : '#dc2626' }}>
                {effectiveDecisaoNaoLabel}
              </span>
              <span style={{ fontSize: '8px', color: layoutType === 'neon' || layoutType === 'blueprint' ? 'rgba(255,255,255,0.55)' : layoutType === 'kanban' ? '#71717a' : layoutType === 'postit' && postItDarkBg ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
                {effectiveDecisaoNaoPct}%
              </span>
            </div>
          </div>
        </>
      )}

      {/* --- MENU SIM (Ramificação Direita) - Diamond Satellite --- */}
      {(onAddNext || onStartConnection) && isHovered && !isDragging && !isConnecting && (
        <div
          ref={menuYesRef}
          data-menu-yes={data.id}
          className="absolute nodrag bg-[var(--bg-surface)] shadow-lg rounded grid grid-cols-2 grid-rows-2 gap-0 p-0 overflow-hidden"
          style={{
            left: data.layoutType === 'classic' 
              ? `calc(50% + ${decisionSize / 2}px - 20px - 4.48px - 28px + 2.8px)` // Estilo clássico: 50% para esquerda + 5% para direita (28px = 50% de 56px, 2.8px = 5% de 56px)
              : `calc(50% + ${decisionSize / 2}px - 20px - 4.48px + 2.8px)`, // Vértice direito do losango - overlap - 8% para esquerda + 5% para direita (56px * 0.08 = 4.48px, 2.8px = 5% de 56px)
            top: 'calc(50% - 28px)', // Centraliza verticalmente (56px/2 = 28px)
            width: '56px', // w-14
            height: '56px', // h-14
            borderWidth: '0.5px',
            borderColor: 'var(--border-default)',
            transform: 'rotate(45deg)', // Rotaciona o container 45 graus
            zIndex: -1, // Fica atrás do losango
            opacity: isMenuYesAnimating ? 0 : 1,
            transition: 'opacity 150ms ease-out',
          }}
          onMouseEnter={() => {
            setShowAddMenuYes(true);
          }}
          onMouseLeave={(e) => {
            const relatedTarget = e.relatedTarget as HTMLElement;
            const stayingInCard = relatedTarget?.closest(`[data-card-id="${data.id}"]`);
            if (!stayingInCard) {
              setShowAddMenuYes(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Quadrante Superior Esquerdo - Decisão (verde) */}
          <button
            className="w-full h-full flex items-center justify-center hover:bg-green-100 hover:shadow-inner transition-all duration-200 cursor-pointer group rounded-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowAddMenuYes(false);
              onAddNext(data.id, 'decisao', true);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            title="Adicionar Decisão"
          >
            <Diamond size={14} className="text-green-500 group-hover:scale-110 transition-transform" style={{ transform: 'rotate(-45deg)' }} />
          </button>
          
          {/* Quadrante Superior Direito - Atividade (azul) */}
          <button
            className="w-full h-full flex items-center justify-center hover:bg-blue-100 hover:shadow-inner transition-all duration-200 cursor-pointer group rounded-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowAddMenuYes(false);
              onAddNext(data.id, 'atividade', true);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            title="Adicionar Etapa"
          >
            <Square size={14} className="text-blue-500 group-hover:scale-110 transition-transform" fill="currentColor" style={{ transform: 'rotate(-45deg)' }} />
          </button>

          {/* Quadrante Inferior Esquerdo - Vazio/Inativo (escondido debaixo do nó) */}
          <div className="w-full h-full bg-[var(--bg-surface)] opacity-50 cursor-not-allowed flex items-center justify-center rounded-none" />

          {/* Quadrante Inferior Direito - Conexão */}
          <button
            className={`w-full h-full flex items-center justify-center transition-all duration-200 group rounded-none ${
              onStartConnection 
                ? 'hover:bg-slate-100 hover:shadow-inner cursor-pointer' 
                : 'opacity-50 cursor-not-allowed'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (onStartConnection) {
                setShowAddMenuYes(false);
                onStartConnection(data.id, 'right');
              }
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (onStartConnection) {
                e.preventDefault();
              }
            }}
            disabled={!onStartConnection}
            title={onStartConnection ? "Conectar manualmente" : "Conexão Não disponível"}
          >
            <LinkIcon 
              size={14} 
              className={onStartConnection ? "text-slate-500 group-hover:scale-110 transition-transform" : "text-slate-300"} 
              style={{ transform: 'rotate(-45deg)' }} 
            />
          </button>
        </div>
      )}

      {/* --- MENU NÃO (Ramificação Inferior) - Diamond Satellite --- */}
      {(onAddNext || onStartConnection) && isHovered && !isDragging && !isConnecting && (
        <div
          ref={menuNoRef}
          data-menu-no={data.id}
          className="absolute nodrag bg-[var(--bg-surface)] shadow-lg rounded grid grid-cols-2 grid-rows-2 gap-0 p-0 overflow-hidden"
          style={{
            left: 'calc(50% - 28px)', // Centraliza horizontalmente (56px/2 = 28px)
            top: data.layoutType === 'classic'
              ? `calc(50% + ${decisionSize / 2}px - 20px - 4.48px - 28px + 2.8px)` // Estilo clássico: 50% para cima + 5% para baixo (28px = 50% de 56px, 2.8px = 5% de 56px)
              : `calc(50% + ${decisionSize / 2}px - 20px - 4.48px + 2.8px)`, // Vértice inferior do losango - overlap - 8% para cima + 5% para baixo (56px * 0.08 = 4.48px, 2.8px = 5% de 56px)
            width: '56px', // w-14
            height: '56px', // h-14
            borderWidth: '0.5px',
            borderColor: 'var(--border-default)',
            transform: 'rotate(45deg)', // Rotaciona o container 45 graus
            zIndex: -1, // Fica atrás do losango
            opacity: isMenuNoAnimating ? 0 : 1,
            transition: 'opacity 150ms ease-out',
          }}
          onMouseEnter={() => {
            setShowAddMenuNo(true);
          }}
          onMouseLeave={(e) => {
            const relatedTarget = e.relatedTarget as HTMLElement;
            const stayingInCard = relatedTarget?.closest(`[data-card-id="${data.id}"]`);
            if (!stayingInCard) {
              setShowAddMenuNo(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Quadrante Superior Esquerdo - Inativo (escondido debaixo do nó) */}
          <div className="w-full h-full bg-[var(--bg-surface)] opacity-50 cursor-not-allowed flex items-center justify-center rounded-none" />

          {/* Quadrante Superior Direito - Decisão (verde) */}
          <button
            className="w-full h-full flex items-center justify-center hover:bg-green-100 hover:shadow-inner transition-all duration-200 cursor-pointer group rounded-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowAddMenuNo(false);
              onAddNext(data.id, 'decisao', false);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            title="Adicionar Decisão"
          >
            <Diamond size={14} className="text-green-500 group-hover:scale-110 transition-transform" style={{ transform: 'rotate(-45deg)' }} />
          </button>
          
          {/* Quadrante Inferior Esquerdo - Conexão */}
          {onStartConnection && (
            <button
              className="w-full h-full flex items-center justify-center hover:bg-slate-100 hover:shadow-inner transition-all duration-200 cursor-pointer group rounded-none"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddMenuNo(false);
                onStartConnection(data.id, 'bottom');
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Conectar manualmente"
            >
              <LinkIcon size={14} className="text-slate-500 group-hover:scale-110 transition-transform" style={{ transform: 'rotate(-45deg) scaleX(-1)' }} />
            </button>
          )}
          
          {/* Quadrante Inferior Direito - Atividade (azul) */}
          <button
            className="w-full h-full flex items-center justify-center hover:bg-blue-100 hover:shadow-inner transition-all duration-200 cursor-pointer group rounded-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowAddMenuNo(false);
              onAddNext(data.id, 'atividade', false);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            title="Adicionar Etapa"
          >
            <Square size={14} className="text-blue-500 group-hover:scale-110 transition-transform" fill="currentColor" style={{ transform: 'rotate(-45deg)' }} />
          </button>
        </div>
      )}
    </div>
  );
};
